import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Wand2, Trash2, ShieldAlert, CheckCircle2, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SEVERITY_TONES = {
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  major: "bg-amber-100 text-amber-800 border-amber-200",
  minor: "bg-sky-100 text-sky-800 border-sky-200",
  informational: "bg-slate-100 text-slate-800 border-slate-200",
};

const CATEGORY_LABELS = {
  auth: "Authentication",
  data_quality: "Data Quality",
  permissions: "Permissions",
  shift_freeze: "Shift Freeze",
  website: "Website (SBT Site)",
  site_behavior: "Site UX Behavior",
  misc: "Misc",
};

export default function AdminSystemHealth() {
  const [data, setData] = useState({ count: 0, summary: {}, checks_registered: [], anomalies: [] });
  const [loading, setLoading] = useState(true);
  const [auditRunning, setAuditRunning] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/self-heal/anomalies?limit=500${showHistory ? "&resolved=true" : ""}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load anomalies");
    } finally {
      setLoading(false);
    }
  }, [showHistory]);
  useEffect(() => { load(); }, [load]);

  const runAudit = async (autoFix) => {
    setAuditRunning(true);
    try {
      const { data: r } = await api.post(`/self-heal/audit?auto_fix=${autoFix ? "true" : "false"}`);
      toast.success(
        `Audit scanned ${r.scanned_checks} checks · ${r.anomalies_found} anomalies · ${r.auto_fixed} auto-fixed`,
        { duration: 8000 },
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Audit failed");
    } finally {
      setAuditRunning(false);
    }
  };

  const fixOne = async (aid) => {
    setFixingId(aid);
    try {
      const { data: r } = await api.post(`/self-heal/fix/${aid}`);
      if (r.ok) {
        toast.success(r.detail || "Fixed");
      } else {
        toast.error(r.detail || "Fix failed");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fix failed");
    } finally {
      setFixingId(null);
    }
  };

  const clearResolved = async () => {
    if (!window.confirm("Delete all resolved anomaly records? This just tidies up the panel — no functional impact.")) return;
    try {
      const { data: r } = await api.post("/self-heal/anomalies/clear", { resolved_only: true });
      toast.success(`Cleared ${r.deleted} resolved records`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const a of (data.anomalies || [])) {
      const cat = a.category || "misc";
      if (!g[cat]) g[cat] = [];
      g[cat].push(a);
    }
    return g;
  }, [data.anomalies]);

  return (
    <div data-testid="admin-system-health">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <ShieldAlert size={18} className="text-rose-600" /> System Health &amp; Self-Heal
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-2xl">
            Autonomous — no admin approval required. The framework auto-sweeps at boot (30 s after restart), every hour (:07), and again nightly at 05:00 IST. Safe fixes (stale sessions, inactive lead assignees, orphan productivity rows, broken campaign attachments) are applied instantly. Complex issues (duplicate leads, orphan users, missing PDFs, duplicate slugs) surface here for admin judgement.
            <span className="ml-2 font-mono">{data.checks_registered.length} checks registered</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => runAudit(false)} disabled={auditRunning} data-testid="sh-audit-detect-btn">
            {auditRunning ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}Detect only
          </Button>
          <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={() => runAudit(true)} disabled={auditRunning} data-testid="sh-audit-heal-btn">
            {auditRunning ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}Detect + Auto-fix
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm border-sky-300 text-sky-700 hover:bg-sky-50"
            onClick={async () => {
              if (!window.confirm("Preview normalize? This will scan for duplicate Lead IDs and show a plan. You'll be asked again before applying.")) return;
              try {
                const { data: dry } = await api.post("/leads/normalize-ids?dry_run=true");
                if (dry.leads_renumbered === 0) {
                  toast.success("No duplicate Lead IDs found — sequence is already clean.");
                  return;
                }
                if (!window.confirm(`Found ${dry.duplicates_found} duplicate group(s). Will renumber ${dry.leads_renumbered} lead(s) with fresh IDs at the end of the sequence. Apply now?`)) return;
                const { data: applied } = await api.post("/leads/normalize-ids?dry_run=false");
                toast.success(`Renumbered ${applied.leads_renumbered} lead(s) to resolve duplicates`);
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Normalize failed");
              }
            }}
            data-testid="sh-normalize-lead-ids-btn"
          >
            Normalize Lead IDs
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm text-rose-700" onClick={clearResolved} data-testid="sh-clear-resolved-btn">
            <Trash2 size={12} className="mr-1" />Clear resolved
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm text-amber-700 border-amber-300 hover:bg-amber-50"
            onClick={async () => {
              if (!confirm("Restore every lead assignment that self-heal cleared as 'inactive user'? Skips leads already re-assigned since.")) return;
              try {
                const { data } = await api.post("/self-heal/restore-inactive-assignments");
                toast.success(`Restored ${data.restored} · skipped ${data.skipped_already_assigned} · missing ${data.lead_missing}`);
                await load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
              }
            }}
            data-testid="sh-restore-assignments-btn"
          >
            <Wand2 size={12} className="mr-1" />Restore cleared assignments
          </Button>
          <Button
            size="sm"
            variant={showHistory ? "default" : "outline"}
            className={`rounded-sm ${showHistory ? "bg-slate-800 hover:bg-slate-900" : ""}`}
            onClick={() => setShowHistory((v) => !v)}
            data-testid="sh-toggle-history-btn"
          >
            {showHistory ? "Showing history" : "Show auto-fix history"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin inline mr-1" /> Loading…
        </div>
      ) : data.count === 0 ? (
        <div className="py-10 text-center text-sm text-emerald-700 font-medium">
          <CheckCircle2 size={18} className="inline mr-1" />
          {showHistory ? "No auto-fix history yet — nothing has needed healing." : "No open anomalies. System is healthy."}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {Object.entries(data.summary).map(([k, v]) => (
              <span key={k} className="text-[10px] px-2 py-1 rounded-sm bg-secondary border border-border font-mono">
                {k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
          {Object.entries(grouped).map(([cat, rows]) => (
            <div key={cat} className="mb-6 border border-border rounded-sm bg-card">
              <div className="px-3 py-2 bg-secondary/50 border-b border-border">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {CATEGORY_LABELS[cat] || cat} · {rows.length}
                </span>
              </div>
              <table className="w-full text-xs" data-testid={`sh-table-${cat}`}>
                <thead className="bg-secondary/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5">Kind</th>
                    <th className="text-left px-3 py-1.5">Severity</th>
                    <th className="text-left px-3 py-1.5">Target</th>
                    <th className="text-left px-3 py-1.5">Detail</th>
                    <th className="text-right px-3 py-1.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="border-t border-border" data-testid={`sh-row-${a.id}`}>
                      <td className="px-3 py-1.5 font-medium">{a.label}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${SEVERITY_TONES[a.severity] || "bg-slate-100"}`}>{a.severity}</span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">
                        {a.user_email || a.extra?.role_name || a.extra?.lead_id?.slice(0, 8) || a.extra?.work_email || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] text-muted-foreground truncate max-w-[320px]">
                        {a.extra?.orphan_role && `role="${a.extra.orphan_role}" no longer exists`}
                        {a.extra?.role_name && a.extra?.active_users_count != null && `${a.extra.active_users_count} active users, no schedule defined`}
                        {a.extra?.count && `${a.extra.count} duplicate leads in campaigns: ${(a.extra.campaigns || []).join(", ")}`}
                        {a.extra?.assigned_agent && `Points to inactive: ${a.extra.assigned_agent}`}
                        {a.extra?.orphan_lead_id && `Productivity refs deleted lead ${a.extra.orphan_lead_id.slice(0, 8)}…`}
                        {a.extra?.filename && `Broken attachment: ${a.extra.filename}`}
                        {a.extra?.session_id_prefix && `session=${a.extra.session_id_prefix}… stuck since ${a.extra.last_seen_at || "never"}`}
                        {/* Website anomaly details */}
                        {a.kind === "sbt_whitepaper_missing_pdf" && `Published w/o PDF: ${a.extra?.title || a.extra?.slug || "—"}`}
                        {a.kind === "sbt_content_duplicate_slug" && `${a.extra?.content_kind} slug "${a.extra?.slug}" used ${a.extra?.count}× — titles: ${(a.extra?.titles || []).join(", ")}`}
                        {a.kind === "sbt_missing_seo_meta" && `${a.extra?.page} · ${a.extra?.section_key} missing: ${(a.extra?.missing_fields || []).join(", ")}`}
                        {a.kind === "sbt_contact_submission_stale" && `Unread since ${(a.extra?.created_at || "").slice(0, 10)} — ${a.extra?.name} <${a.extra?.email}>`}
                        {a.kind === "sbt_job_application_backlog" && `Unreviewed since ${(a.extra?.created_at || "").slice(0, 10)} — ${a.extra?.applicant_name} for ${a.extra?.job_id}`}
                        {a.kind === "sbt_site_slow_animation" && `${a.extra?.file} — ${a.extra?.duration_s}s animation (> ${a.extra?.threshold_s}s threshold; capping to ${a.extra?.recommended_s}s)`}
                        {a.kind === "sbt_site_broken_image" && (
                          <span className="font-mono text-[10px]">
                            {a.extra?.page || "?"} · <span className="text-rose-600">{a.extra?.http_status || a.extra?.error || "connect fail"}</span>
                            <br />
                            <a href={a.extra?.probed_url || a.extra?.src} target="_blank" rel="noreferrer noopener" className="text-cyan-700 hover:underline break-all">
                              {(a.extra?.src || a.extra?.probed_url || "").slice(0, 120)}
                            </a>
                          </span>
                        )}
                        {a.kind === "sbt_site_stale_build" && `Source is ${a.extra?.lag_seconds}s newer than built site — auto-rebuild scheduled`}
                        {a.kind === "sbt_site_missing_meta" && `${a.extra?.page} — missing: ${(a.extra?.missing || []).join(", ")}`}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {showHistory ? (
                          <span className="text-[10px] text-emerald-700">
                            <CheckCircle2 size={10} className="inline mr-1" />
                            {a.fixed_at ? new Date(a.fixed_at).toLocaleString() : (a.fix_detail || "Auto-fixed")}
                          </span>
                        ) : a.auto_fixable ? (
                          <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 border-emerald-300 text-emerald-700"
                            onClick={() => fixOne(a.id)}
                            disabled={fixingId === a.id}
                            data-testid={`sh-fix-btn-${a.id}`}
                          >
                            {fixingId === a.id ? <Loader2 size={10} className="animate-spin mr-1" /> : <Wand2 size={10} className="mr-1" />}Fix now
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">Needs admin review</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
